/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: faxSendingApi.ts
 🗓️ Created: 2025-12-03
 🧩 Description:
    API client for Fax Sending operations
────────────────────────────────────────────────────────────────*/

import { buildApiUrl, getApiHeaders } from '../../../config/api';
import type { FaxDevice, SendFaxResponse, FaxJobStatus } from '../types';

/**
 * Fetch available fax devices for outbound sending
 */
export async function fetchOutboundFaxDevices(domainId?: number): Promise<FaxDevice[]> {
  const params = new URLSearchParams();
  if (domainId != null) {
    params.append('domainId', domainId.toString());
  }

  const queryString = params.toString();
  const url = buildApiUrl(`/api/telephony/fax-sending/devices${queryString ? `?${queryString}` : ''}`);

  const response = await fetch(url, {
    method: 'GET',
    headers: getApiHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch fax devices');
  }

  const result = await response.json();
  return result.data || [];
}

/**
 * Send a fax
 */
export async function sendFax(
  faxDeviceId: string,
  destinationNumber: string,
  file: File
): Promise<SendFaxResponse> {
  const url = buildApiUrl('/api/telephony/fax-sending/send');

  // Create FormData for file upload
  const formData = new FormData();
  formData.append('faxDeviceId', faxDeviceId.toString());
  formData.append('destinationNumber', destinationNumber);
  formData.append('file', file);

  const headers = getApiHeaders();
  // Remove Content-Type to let browser set it with boundary for multipart
  delete (headers as any)['Content-Type'];

  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to send fax');
  }

  return await response.json();
}

/**
 * Get fax job status
 */
export async function getFaxJobStatus(jobId: string): Promise<FaxJobStatus> {
  const url = buildApiUrl(`/api/telephony/fax-sending/status/${jobId}`);

  const response = await fetch(url, {
    method: 'GET',
    headers: getApiHeaders(),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch fax job status');
  }

  const result = await response.json();
  return result.data;
}

